import { computed, watch } from 'vue';
import { defineStore, acceptHMRUpdate, storeToRefs } from 'pinia';
import { useToast, useTranslation } from '@/composables/plugins';
import {
  usePricingChangesQuery,
  usePricingChangeMutations,
} from '@/queries/pricingChanges';
import { useIndexById } from '@/composables/useIndexById';
import { useOrderStore } from '@/stores/order';
import { useActiveUserStore } from '@/stores/user';

export const usePricingChanges = defineStore('pricingChanges', () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { activeVariantId } = storeToRefs(useOrderStore());
  const { isLoggedIn } = storeToRefs(useActiveUserStore());

  const { pricingChanges, query } = usePricingChangesQuery(activeVariantId, {
    enabled: isLoggedIn,
  });
  const mutations = usePricingChangeMutations(activeVariantId);

  const isLoading = computed(() => query.isLoading.value);
  const isError = computed(() => query.isError.value);

  const hasPendingPricingChanges = computed(() =>
    pricingChanges.value.some((pc) => pc.status === 'pending')
  );

  const getById = useIndexById(pricingChanges);

  // Watch the error object, not isError: a query that keeps failing on
  // background refetches would otherwise re-toast every time.
  watch(query.error, (error) => {
    if (error) toast.error(t('common:errors.genericRequestFail'));
  });

  return {
    pricingChanges,
    isLoading,
    isError,
    hasPendingPricingChanges,
    getById,
    ...mutations,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePricingChanges, import.meta.hot));
}
